/**
 * useCurrentSession — FW-console-entra-giris. `useAuthFlowState.ts` ile AYNI
 * desen (modul-kapsamli, tekil `reactive()` — Pinia YOK, bkz. o dosyanin
 * basligindaki gerekce): router guard'in (router/sessionGuard.ts) cozdugu
 * oturum, HomeView/LoginView'in AYNI agdan IKINCI KEZ sormadan okuyabilmesi
 * icin burada paylasilir.
 *
 * `resolve()` disaridan bir `ApiClient` alir (Vue `inject()`'ine BAGLI
 * DEGILDIR — bkz. sessionOutcome.ts basligi): bilesen agacinin icinde
 * (`useApiClient()`'in sonucu verilerek) VE main.ts'teki router guard'da
 * (createSurfaceApp()'in dondurdugu GERCEK istemciyle) AYNI kod calisir.
 *
 * Authenticated sonuçları mevcut önbelleği kullanmayı sürdürür. Guard terminal
 * hedefe yönlendirirken çözülmüş sonucu tekrar kullanır. Reset, bekleyen
 * sorguları geçersiz kılar; geç gelen yanıt sunucu çıkışından sonra oturumu
 * geri getiremez veya yeni sorgunun tek-uçuş kaydını temizleyemez.
 */
import { reactive, readonly, type DeepReadonly } from 'vue'
import type { ApiClient } from '@fusapp-work/api'
import { resolveSessionOutcome, type SessionResponse } from '../domain/sessionOutcome'

export type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'forbidden' | 'error'

interface CurrentSessionStateData {
  status: SessionStatus
  session: SessionResponse | null
  errorMessage: string | null
}

function createInitialState(): CurrentSessionStateData {
  return { status: 'idle', session: null, errorMessage: null }
}

const state = reactive<CurrentSessionStateData>(createInitialState())
let generation = 0
let inflight: Promise<CurrentSessionStateData> | null = null

export interface UseCurrentSession {
  readonly state: DeepReadonly<CurrentSessionStateData>
  /** Zaten 'authenticated' ise agi TEKRAR sormadan mevcut durumu doner. */
  resolve(apiClient: ApiClient): Promise<DeepReadonly<CurrentSessionStateData>>
  reset(): void
  /** A rejected application session stays terminal until an explicit new login. */
  expire(): void
}

export function useCurrentSession(): UseCurrentSession {
  async function resolve(apiClient: ApiClient): Promise<CurrentSessionStateData> {
    if (state.status === 'authenticated') return state
    if (inflight) return inflight

    state.status = 'loading'
    state.errorMessage = null

    const requestGeneration = generation
    inflight = resolveSessionOutcome(apiClient)
      .then((outcome) => {
        if (requestGeneration !== generation) return state
        if (outcome.kind === 'authenticated') {
          state.status = 'authenticated'
          state.session = outcome.session
          state.errorMessage = null
        } else if (outcome.kind === 'unauthenticated') {
          state.status = 'unauthenticated'
          state.session = null
          state.errorMessage = null
        } else if (outcome.kind === 'forbidden') {
          state.status = 'forbidden'
          state.session = outcome.session
          state.errorMessage = null
        } else {
          state.status = 'error'
          state.session = null
          state.errorMessage = outcome.message
        }
        return state
      })
      .finally(() => {
        if (requestGeneration === generation) inflight = null
      })

    return inflight
  }

  function reset(): void {
    generation += 1
    Object.assign(state, createInitialState())
    inflight = null
  }

  function expire(): void {
    generation += 1
    inflight = null
    Object.assign(state, { status: 'unauthenticated', session: null, errorMessage: null })
  }

  return { state: readonly(state) as DeepReadonly<CurrentSessionStateData>, resolve, reset, expire }
}
